/**
 * <AccessGate> — the app-level approved-user gate.
 *
 * Wraps the entire authenticated app. Given an established Supabase session, it
 * checks the logged-in email against the central approved-user list
 * (`app_access`, see services/accessControlService). Only 'approved' users see
 * the app; everyone else gets a blocking screen. The gate FAILS CLOSED: a fetch
 * error blocks access rather than leaking the app.
 *
 * This is the second half of the lock. The first half (App.tsx) guarantees a
 * session exists at all; this decides whether that session is allowed in.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ShieldAlert, ShieldCheck, Clock, LogOut, RefreshCw, Ban, ClipboardCheck } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import {
  fetchMyAccess,
  ensurePendingRequest,
  entraFirstSignIn,
  notifyAccessRequested,
  type AccessStatus,
} from '@/services/accessControlService'
import { isEntraAuthEnabled } from '@/lib/entraAuth'
import { supabase } from '@/lib/supabase'
import { useProjectEntitlementStore, resolveProjectAccess } from '@/config/projectEntitlements'
import { lastShareNotice, redeemPendingShareInvite } from '@/services/projectShareService'
import { syncModuleFlags } from '@/config/featureFlagCatalog'
import { useModuleEntitlementStore, isAdminAccessRole } from '@/config/moduleEntitlements'

type GateState =
  | { phase: 'checking' }
  | { phase: 'approved' }
  | { phase: 'blocked'; status: AccessStatus }
  | { phase: 'error'; message: string }
  | { phase: 'tester' }

export function AccessGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const email = user?.email ?? null
  const [state, setState] = useState<GateState>({ phase: 'checking' })
  // The module flag catalog is synced once per signed-in identity (issue #30):
  // an approved administrator registers the catalog rows, anyone else only
  // reads them. Keyed by email so a re-check on token refresh does not repeat
  // it, while a switch to another account (the demo mode switcher) does.
  const flagsSyncedFor = useRef<string | null>(null)

  const check = useCallback(async () => {
    if (!email) {
      // No session email — App.tsx should not have mounted the gate, but fail
      // closed just in case.
      setState({ phase: 'blocked', status: 'pending' })
      return
    }
    // Stale-while-revalidate: only the FIRST check shows the full-screen
    // spinner. Re-checks (e.g. the demo profile switcher swapping the demo
    // session, a token refresh) keep the app rendered while verifying in the
    // background — a blocked/error result still slams the gate. Without this,
    // every profile click blanked the whole page white for the round-trip.
    setState((prev) => (prev.phase === 'approved' ? prev : { phase: 'checking' }))
    // Tester-portal accounts are not Tesseract users: send them to the portal
    // without filing an access request (migration 00519 skips them too).
    if (user?.user_metadata?.account_type === 'tester') {
      setState({ phase: 'tester' })
      return
    }
    try {
      // A project share link waiting from before sign-in: redeem it first, so a
      // new invitee lands approved for that project (the server checks the link).
      await redeemPendingShareInvite()
      let record = await fetchMyAccess(email)
      // Entra builds: the server handles a first (or still pending) sign-in,
      // which may approve the person under a tenant rule. A failure here
      // falls through to the usual pending path, never to approval.
      if (isEntraAuthEnabled() && (!record || record.status === 'pending')) {
        const status = await entraFirstSignIn().catch(() => null)
        if (status) record = await fetchMyAccess(email)
      }
      if (!record) {
        // No row yet — file a pending request and hold at the pending screen.
        const meta = user?.user_metadata ?? {}
        await ensurePendingRequest(
          email,
          (meta.full_name as string) ?? null,
          (meta.organization_name as string) ?? null,
        ).catch(() => { /* trigger likely already filed it */ })
        void notifyAccessRequested()
        setState({ phase: 'blocked', status: 'pending' })
        return
      }
      if (record.status === 'approved') {
        // Per-user module entitlement (migration 00521) is set BEFORE the app
        // renders, so tiles, sidebar and the route guard never flash modules
        // this user is not entitled to.
        const isAdmin = isAdminAccessRole(record.accessRole)
        useModuleEntitlementStore.getState().setEntitlement({
          allowedModules: record.allowedModules ?? null,
          isAdmin,
        })
        // Project entitlement (00541), also before the app renders, so no list
        // ever shows a project this user may not see. Admins are never restricted.
        // A read-only agile grant (00582) also makes its project visible.
        const { projectIds, agileReadOnlyIds, agileOnlyIds } = resolveProjectAccess({
          isAdmin,
          allowedProjects: record.allowedProjects,
          agileReadOnlyProjects: record.agileReadOnlyProjects,
        })
        let portfolioIds: string[] | null = null
        if (projectIds && projectIds.length) {
          const { data: rows } = await supabase.from('projects').select('portfolio_id').in('id', projectIds)
          portfolioIds = [...new Set((rows ?? []).map((r) => r.portfolio_id as string | null).filter((p): p is string => !!p))]
        } else if (projectIds) {
          portfolioIds = []
        }
        useProjectEntitlementStore.getState().setProjectEntitlement({ projectIds, portfolioIds, agileReadOnlyIds, agileOnlyIds })
        setState({ phase: 'approved' })
        if (flagsSyncedFor.current !== email) {
          flagsSyncedFor.current = email
          void syncModuleFlags(record).catch((err: unknown) => {
            console.warn(
              '[featureFlagCatalog] module flag catalog sync failed:',
              err instanceof Error ? err.message : err,
            )
          })
        }
      } else {
        // Pending: make sure the administrators have been told (sent once per request).
        if (record.status === 'pending') void notifyAccessRequested()
        setState({ phase: 'blocked', status: record.status })
      }
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : 'Access check failed' })
    }
  }, [email, user])

  useEffect(() => {
    void check()
  }, [check])

  if (state.phase === 'approved') return <>{children}</>

  if (state.phase === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
          <p className="text-body-sm text-text-secondary">Checking your access…</p>
        </div>
      </div>
    )
  }

  if (state.phase === 'error') {
    return (
      <GateScreen
        icon={<ShieldAlert size={28} />}
        tone="amber"
        title="Could not verify access"
        body="We could not confirm your access just now. Check your connection and try again."
        detail={state.message}
        email={email}
        onRetry={check}
      />
    )
  }

  if (state.phase === 'tester') {
    return (
      <GateScreen
        icon={<ClipboardCheck size={28} />}
        tone="blue"
        title="This is a tester account"
        body="Tester accounts execute test scripts on the tester portal and do not open the rest of Tesseract XPM."
        email={email}
        onRetry={() => { window.location.href = '/test-portal' }}
        retryLabel="Go to the tester portal"
      />
    )
  }

  // blocked
  if (state.status === 'suspended') {
    return (
      <GateScreen
        icon={<Ban size={28} />}
        tone="red"
        title="Access revoked"
        body="Your access to Tesseract XPM has been suspended. Contact your administrator if you believe this is an error."
        email={email}
        onRetry={check}
      />
    )
  }

  // A share link that could not be used (expired, not valid) explains the wait.
  const share = lastShareNotice()
  return (
    <GateScreen
      icon={<Clock size={28} />}
      tone="blue"
      title="Access pending approval"
      body="Your account is registered and awaiting administrator approval. You will be able to sign in as soon as an administrator approves your request."
      detail={share && !share.ok ? `${share.error ?? 'Your share link could not be used.'} Your request has gone to the administrators instead.` : undefined}
      email={email}
      onRetry={check}
    />
  )
}

// ---------------------------------------------------------------------------

type Tone = 'blue' | 'amber' | 'red'

const TONES: Record<Tone, { ring: string; icon: string }> = {
  blue: { ring: 'bg-brand-50 text-brand-600', icon: 'text-brand-600' },
  amber: { ring: 'bg-amber-50 text-amber-600', icon: 'text-amber-600' },
  red: { ring: 'bg-red-50 text-red-600', icon: 'text-red-600' },
}

function GateScreen({
  icon,
  tone,
  title,
  body,
  detail,
  email,
  onRetry,
  retryLabel,
}: {
  icon: React.ReactNode
  tone: 'blue' | 'amber' | 'red'
  title: string
  body: string
  detail?: string
  email: string | null
  onRetry: () => void
  retryLabel?: string
}) {
  const signOut = useAuthStore((s) => s.signOut)
  const t = TONES[tone]
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-500 text-white mb-4">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-heading-lg font-display text-text-primary">Tesseract XPM</h1>
          <p className="text-body-sm text-text-secondary mt-1">Project &amp; Portfolio Management</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-border p-8 text-center">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${t.ring}`}>
            <span className={t.icon}>{icon}</span>
          </div>
          <h2 className="text-heading-md font-display text-text-primary mb-2">{title}</h2>
          <p className="text-body-sm text-text-secondary mb-4">{body}</p>
          {email && (
            <p className="text-[12px] text-text-tertiary mb-4">
              Signed in as <span className="font-medium text-text-secondary">{email}</span>
            </p>
          )}
          {detail && (
            <p className="text-[11px] text-red-500/80 mb-4 break-words">{detail}</p>
          )}
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-body-sm font-medium hover:bg-brand-600 transition-colors"
            >
              <RefreshCw size={15} /> {retryLabel ?? 'Check again'}
            </button>
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-text-secondary text-body-sm font-medium hover:bg-surface-muted transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
